<template>
  <div class="nx-form-navigation-url-wrapper">
    <v-select
      :attach="shouldAttach()"
      class="mt-3"
      :items="items"
      :label="$t('message.wizard.navigation.chooseType')"
      :value="type"
      :disabled="disabled"
      @input="updateType"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    >
      <template slot="item" slot-scope="{ item }">
        <v-list-tile-avatar>
          <v-icon v-text="item.icon"></v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title v-html="item.text"></v-list-tile-title>
        </v-list-tile-content>
      </template>

      <template slot="selection" slot-scope="{ item, selected }">
        <v-chip
          :selected="selected">
          <v-avatar>
            <v-icon v-text="item.icon"></v-icon>
          </v-avatar>
          <span v-text="item.text"></span>
        </v-chip>
      </template>
    </v-select>

    <v-text-field
      :label="$t('message.form.fields.url')"
      :value="draft.url"
      @input="updateField('url', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      name="url"
      v-if="type === 'manual'"
      key="manual-selector"
      :rules="urlValidation"
    ></v-text-field>

    <nx-board-selector
      :additional-filters="{behaviour: ['landingPage', 'taxonHome']}"
      :label="$t('message.wizard.navigation.chooseBoard')"
      :limit="1"
      :value="draft.entity"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @input="updateEntityIdValue"
      disable-board-creation
      key="board-selector"
      v-else-if="type === 'board'"
      validation="required"
    ></nx-board-selector>

    <nx-content-selector
      :label="$t('message.wizard.navigation.chooseContent')"
      :limit="1"
      :type="contentSelectorTypes"
      :value="draft.entity"
      :allow-edit-items="false"
      @input="updateEntity"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      v-else-if="type === 'content'"
      key="content-selector"
      validation="required"
    ></nx-content-selector>

    <nx-tag-selector
      :label="$t('message.wizard.navigation.chooseTag')"
      :limit="1"
      :additional-filters="{active: 'yes'}"
      :value="draft.entity"
      @input="updateEntityIdValue"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      v-else-if="type === 'tag'"
      key="tag-selector"
      validation="required"
    ></nx-tag-selector>

    <component
      v-else
      :is="selectedItem.component"
      v-bind="selectedItem.props"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @input="updateEntity"
      :value="draft.entity"
    />

    <v-layout v-if="showFollow">
      <v-flex class="xs12 sm6">
        <v-checkbox
          :label="$t('message.form.fields.target')"
          :input-value="draft.target"
          @change="updateField('target', Boolean($event))"
          class="my-0 pt-2"
          v-if="type !== 'empty'"
        ></v-checkbox>
      </v-flex>

      <v-flex class="xs12 sm6">
        <v-switch
          v-for="(option, key) in relOptions"
          :key="`rel-option-${key}`"
          :label="$t(`message.form.fields.${option.label}`)"
          :input-value="draft[option.field]"
          @change="updateField(option.field, Boolean($event))"
          class="my-0 pt-2"
          v-if="type !== 'empty'"
        ></v-switch>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { isArray } from 'lodash';
import { mapState } from 'vuex';
import BoardSelector from '@/modules/board/selector/Selector';
import ContentSelector from '@/modules/content/selector/Selector';
import TagSelector from '@/modules/taxonomy/selector/TagSelector';
import TaxonomySelector from '@/modules/taxonomy/selector/Selector';
import navigationConfig from '@/modules/navigation/config';
import { getContentsWithLandingPage } from '@/modules/content/config';
import { elementHasFixedOrStickyPosition } from '@/utils';

export default {
  name: 'nx-navigation-url',
  extends: {},
  mixins: [],
  components: {
    [TaxonomySelector.name]: TaxonomySelector,
    [TagSelector.name]: TagSelector,
    [ContentSelector.name]: ContentSelector,
    [BoardSelector.name]: BoardSelector,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    showFollow: {
      type: Boolean,
      default: () => true,
    },
    excludedTypes: {
      type: Array,
      default: () => [],
    },
    defaultType: {
      type: String,
      default: () => 'manual',
    },
    allowJsCalls: {
      type: Boolean,
      default: () => true,
    },
    customUrlTypes: {
      type: Array,
      default: () => [],
    },
    customContentTypes: {
      type: Array,
      default: () => null,
    },
  },
  data() {
    return {
      draft: {},
      type: this.defaultType,
    };
  },
  mounted() {
    // Emit draft on mounted to trigger default value input
    this.emit(this.draft);
  },
  computed: {
    ...mapState(['site']),
    contentSelectorTypes() {
      if (isArray(this.customContentTypes)) {
        return this.customContentTypes;
      }
      return getContentsWithLandingPage();
    },
    siteExcludedTypes() {
      if (Object.keys(navigationConfig.siteExcludedTypes)
        .includes(this.site.idAlias)) {
        return navigationConfig.siteExcludedTypes[this.site.idAlias];
      }
      return [];
    },
    items() {
      if (this.customUrlTypes.length > 0) {
        return Object.values(navigationConfig.navigationUrlConfig)
          .filter(config => this.customUrlTypes.includes(config.value));
      }
      return Object.values(navigationConfig.navigationUrlConfig)
        .filter(config => !this.excludedTypes.includes(config.value))
        .filter(config => !this.siteExcludedTypes.includes(config.value));
    },
    urlValidation() {
      // eslint-disable-next-line no-useless-escape
      const validUrl = new RegExp('^((?:https?:\/\/|\/\/)[a-zA-Z0-9-.]+(:\\d+)?)?((\/.*)?\/(?!\/).*)?$', 'i');
      // eslint-disable-next-line
      const javascriptCall = new RegExp('(javascript:).*\(\)');
      return [() => {
        if (!this.draft) {
          return this.$t('message.upload.error.invalidURL');
        }
        if (this.allowJsCalls) {
          const valid = javascriptCall.test(this.draft.url) || validUrl.test(this.draft.url);
          return valid ? true : this.$t('message.upload.error.invalidURLorScript');
        }
        return validUrl.test(this.draft.url) ? true : this.$t('message.upload.error.invalidURL');
      }];
    },
    display() {
      return this.items.map(item => item.type)
        .reduce((acc, type) => {
          acc[type] = this.type === type;
          return acc;
        }, {});
    },
    selectedItem() {
      if (!this.type) {
        return null;
      }
      return navigationConfig.navigationUrlConfig[this.type];
    },
    relOptions() {
      return navigationConfig.relOptions;
    },
  },
  methods: {
    shouldAttach() {
      return elementHasFixedOrStickyPosition(this.$el);
    },
    updateEntity(entity) {
      this.$set(this.draft, 'entity', navigationConfig.filterNavigationEntityAttributes(entity));
      this.$set(this.draft, 'url', (entity || { publicUrl: '' }).publicUrl || (entity || { host: '' }).host);
      this.emit(this.draft);
    },
    updateEntityIdValue(id, entity) {
      this.updateEntity(entity);
    },
    updateField(field, value) {
      this.$set(this.draft, field, value);
      this.emit(this.draft);
    },
    updateType(type) {
      this.draft.entity = null;
      this.updateField('type', type);
      this.type = type;
    },
    emit(value) {
      this.$emit('input', value);
    },
  },
  watch: {
    value: {
      handler() {
        this.draft = cloneDeep(typeof this.value === 'object' ? this.value : {});
        this.draft.type = this.draft.type || this.defaultType;

        // Assign default value to `rel` attribute options.
        Object.values(navigationConfig.relOptions)
          .filter(option => option.default !== undefined)
          .forEach((option) => {
            this.draft[option.field] = option.default;
          });

        if (navigationConfig.defaultLinkTarget) {
          this.draft.target = navigationConfig.defaultLinkTarget;
        }
        if (navigationConfig.defaultLinkFollow) {
          this.draft.follow = navigationConfig.defaultLinkFollow;
        }
        this.type = this.draft.type;
      },
      immediate: true,
    },
  },
};
</script>
